import { Box } from "@chakra-ui/react";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { Seomatic } from "react-seomatic";

import ContentSections from "@components/ContentSections";
import PressHeader from "@components/PressHeader";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import { returnStaticProps } from "@lib/returnStaticProps";
import craft from "@pages/api/craft";
import QueryItem from "@queries/news/QueryItem.graphql";
import QuerySlugs from "@queries/QuerySlugs.graphql";

import {
  ComponentRenderData,
  extractPlasmicQueryData, PlasmicComponent, PlasmicRootProvider
} from "@plasmicapp/loader-nextjs";

import { PLASMIC } from "@abnormal/plasmic-init";

export const getStaticPaths: GetStaticPaths = async () => {
	// Fetch every possible path
	const response = await craft(QuerySlugs, { section: "news", limit: 20 });

	const paths = [];
	response.entries.map((item) => {
		console.log(`Generating /news/${item.slug}`);

		paths.push({
			params: { slug: item.slug }
		});
	});

	return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;

	// Construct uri from the slug array
	const { slug } = params;

	// What is the uri
	const uri = `/about/news/${slug}`;

	//fetch demo blade from plasmic
	const plasmicData = await PLASMIC.maybeFetchComponentData([
		"BladesDynamicDemoBlade",
		"Footer"
	], {
		// deferChunks: true,
	});
	const compMeta = plasmicData.entryCompMetas[0];

	// Cache the necessary data fetched for the page
	const queryCache = await extractPlasmicQueryData(
		<PlasmicRootProvider
			loader={PLASMIC}
			prefetchedData={plasmicData}
			pageParams={compMeta.params}
		>
			<PlasmicComponent component={compMeta.displayName} />
		</PlasmicRootProvider>
	);
	// Fetch the page's data
	const response = await craft(
		QueryItem,
		{ slug, excludeSlug: `not ${slug}` },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		item,
		resources,
		primaryNavigation,
		primaryCallToActions,
		persistentNav
	} = response;

	// Return props if entry exists, otherwise check Retour for redirects or 404
	const returnResponse = await returnStaticProps(
		{
			navigation: {
				primaryNavigation,
				primaryCallToActions
			},
			pageEntry,
			resources,
			persistentNav,
			item,
			plasmicData,
			compMeta,
			queryCache
		},
		!item,
		uri
	);

	return returnResponse;
};

interface IProps {
	pageEntry: any;
	item: any;
	plasmicData: ComponentRenderData;
	queryCache: Record<string, any>;
}

export default function Page({
	pageEntry,
	item,
	plasmicData,
	queryCache
}: IProps): JSX.Element {
	const router = useRouter();
  const [isRedirecting, setIsRedirecting] = useState(false);
	const compMeta = plasmicData.entryCompMetas[0];
	useSetHeaderTheme("dark");

  useEffect(() => {
    // Redirect if item is from "News" category and has an external URL
    if (item?.newsTypes[0] && item.newsTypes.some((type) => type.title === "News") &&
      item?.externalUrl && item?.url) {
      setIsRedirecting(true);
      router.push(item.externalUrl);
    }
  }, [item, router]);

	useEffect(() => {
		// change default link styling, all links nested in paragraphs
		const linkNodes = document.querySelectorAll(`p a`);
		Array.from(linkNodes).map((element: HTMLElement) => {
			element.style.setProperty("color", "#6863F2");
		});
	}, []);

	return (
		<>
			<Seomatic Head={Head} {...item.seomatic} />

			<Box backgroundColor="#FFFFFF">
				<PressHeader
					heading={item.heading ? item.heading : item.title}
          summary={item.summary ? item.summary : null}
					url={item.url}
				/>

				<Box
					maxWidth="1225px"
					mx="auto"
					paddingTop="48px"
					paddingBottom="80px"
					paddingX="20px"
				>
					{!!item.contentSections && (
						<ContentSections sections={item.contentSections} />
					)}
          {isRedirecting && (
            <>
              <p style={{
                fontSize:"24px",
                paddingBottom:"36px"
              }}>Redirecting to article...</p>
              <p>
                <a href={item.externalUrl} target="_blank" rel="noopener noreferrer">Click here</a> if not automatically redirected
              </p>
            </>
          )}
				</Box>
			</Box>
			<PlasmicRootProvider
				loader={PLASMIC}
				prefetchedData={plasmicData}
				prefetchedQueryData={queryCache}
				pageParams={compMeta.params}
				pageQuery={router.query}
			>
				<PlasmicComponent
					component={compMeta.displayName}
					projectId={process.env.NEXT_PUBLIC_PLASMIC_DESIGN_SYSTEM_PROJECT_ID}
				/>
			</PlasmicRootProvider>
		</>
	);
}
